'use strict';

/**
 * The single source of truth for renameable product terminology (the "Drafting
 * Kit" vocabulary). Required by both the server (label resolution onto the
 * session) and the client (libLabels), since source/ is bundled for the browser.
 *
 * A Drafting Kit is a VisionType; its overrides live in that VisionType's
 * MetadataManifest.Terms as { <TermKey>: { Singular, Plural, Description } }.
 * resolveTerms() merges those over these defaults to produce the full effective
 * map that the client labels everything from. The DEFAULT_TERMS labels are lifted
 * verbatim from EntityDescriptors (Title/Plural) and SiteMap (nav Names +
 * Descriptions), so behavior is identical when a plan sheet has set no kit.
 *
 * Description is the secondary blurb under a renamed nav item: a craft that
 * renames Test Runs to "Read-throughs" can also say what a Read-through is,
 * instead of inheriting the stock "Executions of routines, step by step."
 */

// The concepts a plan sheet may rename. (KeystoneCustomer is intentionally not
// here -- it stays literal.)
const TERM_KEYS =
[
	'Vision', 'Module', 'Feature', 'WorkItem', 'Routine', 'TestRun',
	'Feedback', 'Whiteboard', 'Documentation', 'Comment', 'Blueprint', 'Deliverable'
];

// Singular / Plural are short labels; Description is the secondary nav blurb.
// Descriptions are lifted verbatim from the SiteMap nav items so the rail reads
// identically when no kit is set; the three non-nav concepts carry sensible text.
const DEFAULT_TERMS =
{
	Vision:        { Singular: 'Vision',     Plural: 'Visions',       Description: 'What you are building and why.' },
	Module:        { Singular: 'Module',     Plural: 'Modules',       Description: 'The parts of the system.' },
	Feature:       { Singular: 'Feature',    Plural: 'Features',      Description: 'What each module does.' },
	WorkItem:      { Singular: 'Work Item',  Plural: 'Work Items',    Description: 'The tasks in flight.' },
	Routine:       { Singular: 'Routine',    Plural: 'Routines',      Description: 'Step-by-step procedures to exercise the product.' },
	TestRun:       { Singular: 'Test Run',   Plural: 'Test Runs',     Description: 'Executions of routines, step by step.' },
	Feedback:      { Singular: 'Feedback',   Plural: 'Feedback',      Description: 'What testers and customers said.' },
	Whiteboard:    { Singular: 'Whiteboard', Plural: 'Whiteboards',   Description: 'A free-form canvas of ideas.' },
	Documentation: { Singular: 'Document',   Plural: 'Documentation', Description: 'The team library.' },
	Comment:       { Singular: 'Comment',    Plural: 'Comments',      Description: 'Notes and discussion.' },
	Blueprint:     { Singular: 'Blueprint',  Plural: 'Blueprints',    Description: 'A work item and everything attached to it.' },
	Deliverable:   { Singular: 'Deliverable', Plural: 'Deliverables',  Description: 'What a customer expects you to deliver.' }
};

// Short labels cap at 64; the Description blurb gets more room (a short sentence).
function _clean(pValue, pMax)
{
	return String(pValue == null ? '' : pValue).trim().slice(0, pMax || 64);
}

/**
 * Merge a kit's term overrides over the defaults. Returns a full map (every key
 * present, with Singular, Plural and Description), so callers never have to fall
 * back per key.
 *
 * @param {object} pKitTerms - { <TermKey>: { Singular?, Plural?, Description? } } or null.
 * @returns {object} full effective terms map.
 */
function resolveTerms(pKitTerms)
{
	let tmpKit = (pKitTerms && typeof pKitTerms === 'object') ? pKitTerms : {};
	let tmpOut = {};
	for (let i = 0; i < TERM_KEYS.length; i++)
	{
		let tmpKey = TERM_KEYS[i];
		let tmpDefault = DEFAULT_TERMS[tmpKey];
		let tmpOverride = (tmpKit[tmpKey] && typeof tmpKit[tmpKey] === 'object') ? tmpKit[tmpKey] : {};
		tmpOut[tmpKey] =
		{
			Singular:    tmpOverride.Singular    ? _clean(tmpOverride.Singular)         : tmpDefault.Singular,
			Plural:      tmpOverride.Plural      ? _clean(tmpOverride.Plural)           : tmpDefault.Plural,
			Description: tmpOverride.Description  ? _clean(tmpOverride.Description, 160)  : tmpDefault.Description
		};
	}
	return tmpOut;
}

module.exports =
{
	TERM_KEYS: TERM_KEYS,
	DEFAULT_TERMS: DEFAULT_TERMS,
	resolveTerms: resolveTerms
};
